import { useEffect, useRef, type ReactNode } from "react";
import type { SessionUser } from "@iltocat/shared";
import { formatBytes, plural } from "../lib/api.js";
import { useOfflineQueue } from "../lib/useOfflineQueue.js";
import { pathOf, type Route } from "../lib/router.js";
import { requestSearchFocus } from "../lib/searchFocus.js";
import { useTheme, type Theme } from "../lib/theme.js";
import { Icon, type IconName } from "./Icon.js";
import { InventorySwitcher } from "./InventorySwitcher.js";
import { Logo } from "./Logo.js";
import { Menu, MenuItem, Segmented } from "@vcnet/ui";
import { PullToRefresh } from "./PullToRefresh.js";

/**
 * The frame every signed-in screen sits in.
 *
 * **Desktop** gets a sidebar: the lockup, a New button, the three places you
 * go (items, locations, filling in details), the few you visit (scan,
 * reports, sharing, admin), and at the foot the inventory switcher, storage, and your
 * account. **A phone** gets a slim top bar and a bottom tab bar with New as a
 * raised button in the middle, where a thumb already is. Same components, one
 * breakpoint, all CSS (docs/notes/theming.md).
 *
 * **New is one tap from anywhere** on both, because the brief asks for it and
 * a sitting is a loop: item, item, item, back to the shelf, item.
 *
 * ⌘K / Ctrl+K puts the cursor in the search box from any screen.
 */

type NavKey =
  | "items"
  | "locations"
  | "enrich"
  | "stock"
  | "receive"
  | "clients"
  | "billing"
  | "reserves"
  | "reports"
  | "sharing"
  | "scan"
  | "import"
  | "settings"
  | "admin";

type NavEntry = { key: NavKey; label: string; icon: IconName; route: Route };

const ITEMS: NavEntry = { key: "items", label: "Items", icon: "grid", route: { name: "home" } };
const LOCATIONS: NavEntry = {
  key: "locations",
  label: "Locations",
  icon: "pin",
  route: { name: "locations", id: null },
};
const ENRICH: NavEntry = {
  key: "enrich",
  label: "Fill in details",
  icon: "sparkles",
  route: { name: "enrich", ids: [] },
};

/**
 * Stage 7's screens, shown only while a stock inventory is the active one -- the
 * roadmap's "the stock workflow never appears in a personal one" is this
 * list being absent, not a screen that says "not here".
 */
const STOCK_NAV: NavEntry[] = [
  { key: "stock", label: "Stock", icon: "box", route: { name: "stock" } },
  { key: "receive", label: "Receive", icon: "download", route: { name: "receive" } },
  { key: "reserves", label: "Reserves", icon: "bookmark", route: { name: "reserves" } },
  { key: "clients", label: "Clients", icon: "globe", route: { name: "clients", id: null } },
  { key: "billing", label: "Billing", icon: "clipboard", route: { name: "billing" } },
];

const SECONDARY: { key: NavKey; label: string; icon: IconName; route: Route }[] = [
  { key: "import", label: "Import", icon: "upload", route: { name: "import" } },
  { key: "scan", label: "Scan", icon: "qr", route: { name: "scan" } },
  { key: "reports", label: "Reports", icon: "chart", route: { name: "reports" } },
  { key: "sharing", label: "Sharing", icon: "users", route: { name: "sharing" } },
  { key: "admin", label: "Admin", icon: "shield", route: { name: "admin" } },
];

/** Which nav entry a screen belongs to. An item and its listing are part of Items. */
function sectionOf(route: Route): NavKey | null {
  switch (route.name) {
    case "home":
    case "search":
    case "item":
    case "listing":
      return "items";
    case "locations":
    case "sort":
      return "locations";
    case "enrich":
      return "enrich";
    case "stock":
    case "receive":
    case "clients":
    case "billing":
    case "reserves":
    case "reports":
    case "sharing":
    case "scan":
    case "import":
    case "admin":
      return route.name;
    // Export lives on the Settings screen, so it lights that tab.
    case "settings":
    case "export":
      return "settings";
    case "labels":
      return route.kind === "item" ? "items" : "locations";
    default:
      return null;
  }
}

/** The shortcut as this keyboard spells it. */
const SHORTCUT = /mac|iphone|ipad/i.test(navigator.userAgent) ? "⌘K" : "Ctrl K";

const initialOf = (user: SessionUser): string =>
  (user.name || user.email).trim().charAt(0).toUpperCase() || "?";

export function AppShell({
  user,
  route,
  onNavigate,
  onSignOut,
  onStorage,
  children,
}: {
  user: SessionUser;
  route: Route;
  onNavigate: (route: Route) => void;
  onSignOut: () => void;
  /** The account's storage, after the offline queue's own background uploads -- the same fact `Capture` and `ItemScreen` report after theirs. */
  onStorage: (usedBytes: number) => void;
  children: ReactNode;
}) {
  const mainRef = useRef<HTMLElement>(null);
  const [theme, setTheme] = useTheme();
  const section = sectionOf(route);
  const queue = useOfflineQueue(onStorage);

  // A stockroom wears utucat stock's violet (tokens.css swaps every accent
  // role on this attribute), so which kind of inventory is on screen is
  // visible before a word is read. Cleared on the way out, so the sign-in
  // screen is always the household blue.
  useEffect(() => {
    const root = document.documentElement;
    if (user.activeKind === "stock") root.dataset["kind"] = "stock";
    else delete root.dataset["kind"];
    return () => {
      delete root.dataset["kind"];
    };
  }, [user.activeKind]);

  // Every screen after the first is a client-side swap, invisible to a screen
  // reader unless something says so. Focus moves to the new screen's content,
  // skipped on the very first render: that one is the browser's own load, and
  // stealing focus from wherever it put it would be the opposite of helpful.
  const firstRender = useRef(true);
  const path = pathOf(route);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    mainRef.current?.focus({ preventScroll: true });
  }, [path]);

  const routeRef = useRef(route);
  routeRef.current = route;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      findAnything();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // `findAnything` reads the route through the ref, so the listener is set once.
  }, []);

  function findAnything() {
    const now = routeRef.current.name;
    if (now !== "home" && now !== "search") onNavigate({ name: "search", query: "" });
    requestSearchFocus();
  }

  const secondary = SECONDARY.filter((entry) => entry.key !== "admin" || user.isAdmin);
  const stockMode = user.activeKind === "stock";
  const primary: NavEntry[] = stockMode
    ? [ITEMS, ...STOCK_NAV, LOCATIONS, ENRICH]
    : [ITEMS, LOCATIONS, ENRICH];
  // The phone has four slots around New. A stockroom's are the two screens a
  // person on the floor opens most; the rest go into More.
  const tabs: [NavEntry, NavEntry, NavEntry] = stockMode
    ? [ITEMS, STOCK_NAV[0]!, STOCK_NAV.find((entry) => entry.key === "billing")!]
    : [ITEMS, LOCATIONS, { ...ENRICH, label: "Details" }];
  const overflow = primary.filter((entry) => !tabs.some((tab) => tab.key === entry.key));
  const used = user.quotaBytes > 0 ? Math.min(1, user.usedBytes / user.quotaBytes) : 1;

  const navButton = (entry: NavEntry, className: string) => (
    <button
      key={entry.key}
      type="button"
      className={className}
      aria-current={section === entry.key ? "page" : undefined}
      onClick={() => onNavigate(entry.route)}
    >
      <Icon name={entry.icon} size={20} />
      <span>{entry.label}</span>
    </button>
  );

  // Only shown once something is actually waiting -- an empty queue is the
  // normal state and has nothing to say. Its own tap retries immediately
  // rather than waiting for the next `online` event, for a connection that
  // came back without the browser noticing.
  const queuePill = queue.count > 0 && (
    <button type="button" className="pill pill-warn" onClick={queue.retry} title="Try uploading now">
      <Icon name="upload" size={13} />
      {plural(queue.count, "item")} waiting to upload
    </button>
  );

  const storage = (
    <div className="storage" title={`${formatBytes(user.usedBytes)} of ${formatBytes(user.quotaBytes)} used`}>
      <div className="storage-row">
        <span>Storage</span>
        <span>
          {formatBytes(user.usedBytes)} / {formatBytes(user.quotaBytes)}
        </span>
      </div>
      <div
        className={used >= 0.9 ? "meter meter-full" : "meter"}
        role="meter"
        aria-label="Storage used"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(used * 100)}
      >
        <span style={{ width: `${Math.max(used * 100, 2)}%` }} />
      </div>
    </div>
  );

  const themePicker = (
    <div className="menu-theme">
      <span>Theme</span>
      <Segmented<Theme>
        label="Theme"
        size="sm"
        value={theme}
        onChange={setTheme}
        options={[
          { value: "auto", label: <Icon name="monitor" size={16} />, title: "Match this device" },
          { value: "light", label: <Icon name="sun" size={16} />, title: "Light" },
          { value: "dark", label: <Icon name="moon" size={16} />, title: "Dark" },
        ]}
      />
    </div>
  );

  const identity = (
    <div className="menu-identity">
      <span className="avatar">{initialOf(user)}</span>
      <span className="menu-identity-text">
        {user.name && <strong>{user.name}</strong>}
        <span>{user.email}</span>
      </span>
    </div>
  );

  const go = (close: () => void, next: Route) => {
    close();
    onNavigate(next);
  };

  return (
    <div className="app">
      <PullToRefresh />
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>

      {/* ------------------------------------------------ desktop sidebar */}
      <aside className="sidebar">
        <button
          className="sidebar-brand"
          type="button"
          aria-label="utucat home"
          onClick={() => onNavigate({ name: "home" })}
        >
          <Logo stock={user.activeKind === "stock"} />
        </button>

        <button
          type="button"
          className="button button-block"
          aria-current={route.name === "capture" ? "page" : undefined}
          onClick={() => onNavigate({ name: "capture" })}
        >
          <Icon name="plus" size={18} />
          New item
        </button>

        <button type="button" className="sidebar-search" onClick={findAnything}>
          <Icon name="search" size={16} />
          <span>Search</span>
          <kbd>{SHORTCUT}</kbd>
        </button>

        <nav className="sidebar-nav" aria-label="Main">
          {primary.map((entry) => navButton(entry, "nav-link"))}
          <span className="nav-divider" aria-hidden="true" />
          {secondary.map((entry) => navButton(entry, "nav-link"))}
        </nav>

        <div className="sidebar-foot">
          <InventorySwitcher user={user} />
          {queuePill}
          {storage}
          <Menu
            label="Account menu"
            placement="up"
            align="start"
            buttonClassName="account-button"
            button={
              <>
                <span className="avatar">{initialOf(user)}</span>
                <span className="account-email">{user.name || user.email}</span>
                <Icon name="chevron-down" size={16} />
              </>
            }
          >
            {(close) => (
              <>
                {identity}
                <MenuItem icon={<Icon name="settings" />} onClick={() => go(close, { name: "settings" })}>
                  Settings
                </MenuItem>
                {themePicker}
                <span className="menu-divider" aria-hidden="true" />
                <MenuItem icon={<Icon name="log-out" />} onClick={onSignOut}>
                  Sign out
                </MenuItem>
              </>
            )}
          </Menu>
        </div>
      </aside>

      {/* --------------------------------------------------- phone top bar */}
      <header className="topbar">
        <button
          className="sidebar-brand"
          type="button"
          aria-label="utucat home"
          onClick={() => onNavigate({ name: "home" })}
        >
          <Logo stock={user.activeKind === "stock"} />
        </button>
        <button type="button" className="icon-button" aria-label="Search" onClick={findAnything}>
          <Icon name="search" size={20} />
        </button>
      </header>

      <main id="main-content" ref={mainRef} tabIndex={-1} className="main">
        <div className="main-inner">
          {user.activeArchived && (
            <p className="archived-banner" role="status">
              <Icon name="lock" size={16} />
              <span>
                This inventory is archived: you can look and export, not change anything. Restore it from
                Settings.
              </span>
            </p>
          )}
          {user.role === "viewer" && !user.activeArchived && (
            <p className="archived-banner" role="status">
              <Icon name="lock" size={16} />
              <span>You have view-only access here: you can look and export, not change anything.</span>
            </p>
          )}
          {children}
        </div>
      </main>

      {/* ------------------------------------------------- phone tab bar */}
      <nav className="tabbar" aria-label="Main">
        {navButton(tabs[0], "tab")}
        {navButton(tabs[1], "tab")}
        <button
          type="button"
          className="tab tab-new"
          aria-label="New item"
          aria-current={route.name === "capture" ? "page" : undefined}
          onClick={() => onNavigate({ name: "capture" })}
        >
          <span className="tab-new-disc">
            <Icon name="plus" size={26} />
          </span>
        </button>
        {navButton(tabs[2], "tab")}
        <Menu
          label="More"
          placement="up"
          buttonClassName={
            secondary.some((entry) => entry.key === section) ||
            overflow.some((entry) => entry.key === section) ||
            section === "settings"
              ? "tab tab-on"
              : "tab"
          }
          button={
            <>
              <Icon name="menu" size={20} />
              <span>More</span>
            </>
          }
        >
          {(close) => (
            <>
              {identity}
              <InventorySwitcher user={user} />
              {queuePill}
              {storage}
              <span className="menu-divider" aria-hidden="true" />
              {[...overflow, ...secondary].map((entry) => (
                <MenuItem
                  key={entry.key}
                  icon={<Icon name={entry.icon} />}
                  onClick={() => go(close, entry.route)}
                >
                  {entry.label}
                </MenuItem>
              ))}
              <MenuItem icon={<Icon name="settings" />} onClick={() => go(close, { name: "settings" })}>
                Settings
              </MenuItem>
              {themePicker}
              <span className="menu-divider" aria-hidden="true" />
              <MenuItem icon={<Icon name="log-out" />} onClick={onSignOut}>
                Sign out
              </MenuItem>
            </>
          )}
        </Menu>
      </nav>
    </div>
  );
}
